export interface CommunitySpaceTemplate {
  slug: string;
  name: string;
  description: string;
  accessType: "free" | "paid" | "tiered";
}

export interface CommunityVerticalTemplate {
  vertical: string;
  label: string;
  spaces: CommunitySpaceTemplate[];
}

const DEFAULT_COMMUNITY_SPACES: readonly CommunitySpaceTemplate[] = [
  {
    slug: "general",
    name: "General",
    description: "Announcements, introductions, and day-to-day discussion for your whole member base.",
    accessType: "free",
  },
  {
    slug: "wins",
    name: "Wins & Progress",
    description: "Celebrate milestones, before-and-after stories, and progress updates from the community.",
    accessType: "free",
  },
  {
    slug: "questions",
    name: "Questions & Support",
    description: "A focused place for product, service, and peer support questions.",
    accessType: "free",
  },
] as const;

export const COMMUNITY_VERTICAL_TEMPLATES: readonly CommunityVerticalTemplate[] = [
  {
    vertical: "salon",
    label: "Salon & Spa",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "tips-tricks",
        name: "Tips & Tricks",
        description: "Styling advice, maintenance tips, and product recommendations from the team.",
        accessType: "free",
      },
      {
        slug: "promotions",
        name: "Promotions",
        description: "Time-sensitive offers, package launches, and member-only specials.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "fitness",
    label: "Fitness & Wellness",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "workout-plans",
        name: "Workout Plans",
        description: "Training structure, session planning, and coach feedback for members.",
        accessType: "free",
      },
      {
        slug: "nutrition",
        name: "Nutrition",
        description: "Meal planning, recovery advice, and healthy habit discussions.",
        accessType: "free",
      },
      {
        slug: "challenges",
        name: "Challenges",
        description: "Weekly and monthly challenges to drive accountability and momentum.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "education",
    label: "Education",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "study-groups",
        name: "Study Groups",
        description: "Peer cohorts, accountability groups, and collaborative learning threads.",
        accessType: "free",
      },
      {
        slug: "resources",
        name: "Resources",
        description: "Shared documents, study materials, and recommended learning references.",
        accessType: "free",
      },
      {
        slug: "alumni",
        name: "Alumni",
        description: "Networking and long-tail support for past students and graduates.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "dental",
    label: "Dental Practice",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "oral-care-tips",
        name: "Oral Care Tips",
        description: "Preventive care guidance, hygiene reminders, and trusted educational content.",
        accessType: "free",
      },
      {
        slug: "treatment-journeys",
        name: "Treatment Journeys",
        description: "Patient-safe treatment milestone updates and expectation-setting discussions.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "medical",
    label: "Medical / Healthcare",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "care-guidance",
        name: "Care Guidance",
        description: "Follow-up education, preparation reminders, and non-diagnostic care guidance.",
        accessType: "free",
      },
      {
        slug: "program-updates",
        name: "Program Updates",
        description: "Updates for active care programs, support sessions, and care pathway reminders.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "legal",
    label: "Legal Services",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "client-education",
        name: "Client Education",
        description: "Plain-language process explainers, prep guidance, and common next steps.",
        accessType: "free",
      },
      {
        slug: "office-updates",
        name: "Office Updates",
        description: "Service updates, policy changes, and upcoming availability announcements.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "real_estate",
    label: "Real Estate",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "market-insights",
        name: "Market Insights",
        description: "Local market observations, buyer and seller updates, and trend explainers.",
        accessType: "free",
      },
      {
        slug: "homeowner-journey",
        name: "Homeowner Journey",
        description: "Move planning, closing milestones, and homeowner success stories.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "restaurant",
    label: "Restaurant / Hospitality",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "special-events",
        name: "Special Events",
        description: "Member nights, tastings, seasonal launches, and event reminders.",
        accessType: "free",
      },
      {
        slug: "menu-insiders",
        name: "Menu Insiders",
        description: "Menu previews, chef notes, and behind-the-scenes hospitality moments.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "agency",
    label: "Marketing Agency",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "campaign-lab",
        name: "Campaign Lab",
        description: "Creative experiments, launch recaps, and campaign learning loops.",
        accessType: "free",
      },
      {
        slug: "office-hours",
        name: "Office Hours",
        description: "Structured Q&A for clients, retainers, and project support follow-through.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "ecommerce",
    label: "E-Commerce",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "product-feedback",
        name: "Product Feedback",
        description: "Customer insight, launch reactions, and product improvement discussions.",
        accessType: "free",
      },
      {
        slug: "drops-launches",
        name: "Drops & Launches",
        description: "Upcoming releases, VIP access windows, and launch-day coordination.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "home_services",
    label: "Home Services",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "project-prep",
        name: "Project Prep",
        description: "Booking prep, site-readiness guidance, and project expectation management.",
        accessType: "free",
      },
      {
        slug: "maintenance-club",
        name: "Maintenance Club",
        description: "Preventive maintenance reminders, seasonal checklists, and member updates.",
        accessType: "free",
      },
    ],
  },
  {
    vertical: "financial",
    label: "Financial Services",
    spaces: [
      ...DEFAULT_COMMUNITY_SPACES,
      {
        slug: "planning-insights",
        name: "Planning Insights",
        description: "Educational planning content, checklists, and client confidence-building updates.",
        accessType: "free",
      },
      {
        slug: "client-briefings",
        name: "Client Briefings",
        description: "Periodic briefings, event reminders, and key timeline updates.",
        accessType: "free",
      },
    ],
  },
] as const;

export function getCommunityTemplateForVertical(vertical: string | null | undefined): CommunityVerticalTemplate | null {
  if (!vertical) return null;
  const normalized = vertical.trim().toLowerCase();
  return COMMUNITY_VERTICAL_TEMPLATES.find((template) => template.vertical === normalized) ?? null;
}

export function getCommunitySpaceTemplate(
  vertical: string | null | undefined,
  slug: string | null | undefined,
): CommunitySpaceTemplate | null {
  const template = getCommunityTemplateForVertical(vertical);
  if (!template || !slug) return null;
  const normalizedSlug = slug.trim().toLowerCase();
  return template.spaces.find((space) => space.slug === normalizedSlug) ?? null;
}
